import { useState, useEffect, useRef, useCallback } from 'react';
import * as Ably from 'ably';
import CrosswordBoard from './CrosswordBoard';
import AmericaSaysBoard from './AmericaSaysBoard';
import GeoGuessBoard from './GeoGuessBoard';
import DropPanBoard from './DropPanBoard';
import ChainReactionBoard from './ChainReactionBoard';
import BracketBoard from './BracketBoard';
import { LITE } from './lite';

const API      = process.env.REACT_APP_API_URL || '/api';
const ABLY_AUTH_URL = `${API}/ably/token`;

// ─── Low-power TV mode ────────────────────────────────────────────────────────
// LITE (from ./lite) is on when the Fire TV / Android TV WebView loads
// /tv?player=android_tv (or ?lite=1). In this mode we shed the most GPU-expensive
// effects so weak TV GPUs hold 60fps.
// Injected once at the TVDisplay root when LITE: kills frosted blur everywhere
// (the #1 frame-killer on Fire TV) app-wide — including inside every game board —
// without editing each component. Panels keep their rgba backgrounds so stay legible.
const LITE_STYLES = LITE
  ? // Balance: keep the display lively (bounces, slides, fades, score pops, timers
    // all still animate) but shed only the GPU-expensive *blurred* effects that
    // re-rasterize every frame on a weak TV GPU:
    //   1) frosted blur everywhere (no motion/liveliness impact),
    //   2) freeze the large ~72vw blurred background orbs/auroras (imperceptible), and
    //   3) freeze the pulsing title glows (animated text/box-shadow blur) — the glow
    //      stays visible, it just stops pulsing. This is the "glow" you noticed.
    `*,*::before,*::after{backdrop-filter:none !important;-webkit-backdrop-filter:none !important;}` +
    // Freeze GPU-expensive *continuous* animations — the orbs/auroras/glows AND any
    // infinite anim that repaints a costly property every frame (filter:blur, box-shadow,
    // background-position, layout). These re-rasterize each frame and are what makes the
    // board stutter on the FireTV GPU when it shares the WebView with the signage player.
    // The element stays visible (it just stops pulsing/sweeping); cheap transform/opacity
    // animations (bounces, slides, fades, score pops, timers) keep running.
    `[style*="Orb"],[style*="orb"],[style*="urora"],[style*="Glow"],[style*="glow"],` +
    `[style*="shimmer"],[style*="scanline"],[style*="gridBreath"],[style*="crHintPulse"],` +
    `[style*="crScan"],[style*="crChainComplete"],[style*="introBgPulse"]{animation:none !important;}` +
    // Also drop the transient blur REVEALS (filter:blur animated in on each clue/word/
    // cell/round entrance). They rasterize a blurred bitmap every frame for ~0.5s and
    // visibly stutter on the FireTV. Elements carry correct resting styles inline
    // (position + opacity:1), so freezing makes them appear cleanly/instantly — no blur,
    // no jank. Cheap fades/pops elsewhere are untouched.
    `[style*="overlayEnter"],[style*="overlayExit"],[style*="gameReveal"],[style*="scatterWordIn"],` +
    `[style*="scatterBannerIn"],[style*="scatterRevealBurst"],[style*="classicReveal"],[style*="flashBurst"],` +
    `[style*="introCountdown"],[style*="circleBreath"],[style*="crLetterDrop"],[style*="crOverlayIn"],` +
    `[style*="crRoundIn"],[style*="cellStamp"]{animation:none !important;}`
  : '';

// Always-on viewport lock for the TV display (scoped to /tv — TVDisplay only mounts
// there, so the admin route is unaffected). Pins the page to the *visible* viewport
// height and hides any scrollbar, so a board that's a hair over 100vh never produces
// a scrollbar or an overscroll sliver — self-correcting on any board/resolution,
// without changing content size.
const TV_VIEWPORT_STYLES =
  `html,body,#root{height:100dvh !important;min-height:0 !important;max-height:100dvh !important;overflow:hidden !important;}` +
  `::-webkit-scrollbar{width:0 !important;height:0 !important;display:none !important;}` +
  `*{scrollbar-width:none !important;}`;

// ─── Shared Ably client ───────────────────────────────────────────────────────
let _ablyClient = null;
function getAblyClient() {
  if (!_ablyClient) _ablyClient = new Ably.Realtime({
    authUrl: ABLY_AUTH_URL,
    autoConnect: true,
    webSocketConnectTimeout: 4000,
    disconnectedRetryTimeout: 3000,
  });
  return _ablyClient;
}

function useAblyChannel(channelName, handlers) {
  const handlersRef = useRef(handlers);
  useEffect(() => { handlersRef.current = handlers; });
  useEffect(() => {
    if (!channelName) return;
    const client  = getAblyClient();
    if (!client) return;
    const channel = client.channels.get(channelName);
    const subs = Object.keys(handlersRef.current).map(event => {
      const cb = (msg) => handlersRef.current[event]?.(msg);
      channel.subscribe(event, cb);
      return { event, cb };
    });
    return () => subs.forEach(({ event, cb }) => channel.unsubscribe(event, cb));
  }, [channelName]);
}

// ─── CSS ──────────────────────────────────────────────────────────────────────
const TV_STYLES = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { overflow: hidden; touch-action: none; }

  @keyframes tvOrb1  { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(60px,-40px) scale(1.2)} }
  @keyframes tvOrb2  { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(-70px,30px) scale(1.15)} }
  @keyframes tvOrb3  { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(50px,60px) scale(1.1)} }
  @keyframes tvStar  { 0%,100%{opacity:.15;transform:scale(1)} 50%{opacity:.9;transform:scale(1.8)} }
  @keyframes tvAurora{ 0%,100%{opacity:.12;transform:scaleX(1)} 50%{opacity:.28;transform:scaleX(1.06)} }
  @keyframes tvPulse { 0%,100%{opacity:1} 50%{opacity:.3} }
  @keyframes tvFadeIn{ from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
  @keyframes tvRing  { 0%{transform:translate(-50%,-50%) scale(0.6);opacity:.9} 100%{transform:translate(-50%,-50%) scale(2.5);opacity:0} }
  @keyframes tvBounce{
    0%{transform:scale(1)}
    30%{transform:scale(1.08)}
    50%{transform:scale(0.97)}
    70%{transform:scale(1.03)}
    100%{transform:scale(1)}
  }
  @keyframes tvGlow  { 0%,100%{text-shadow:0 0 40px rgba(255,179,0,.6),0 0 80px rgba(255,140,0,.3)} 50%{text-shadow:0 0 80px rgba(255,215,0,1),0 0 160px rgba(255,140,0,.6)} }
  @keyframes tvSlide { from{transform:translateX(-120%)} to{transform:translateX(120vw)} }
  @keyframes tvCountBig { 0%{transform:scale(.5);opacity:0} 40%{transform:scale(1.3);opacity:1} 100%{transform:scale(1)} }
  @keyframes tvWobble{ 0%{transform:rotate(-4deg)} 50%{transform:rotate(4deg)} 100%{transform:rotate(-4deg)} }
`;

// ─── Animated Background ──────────────────────────────────────────────────────
function TvBg() {
  if (LITE) {
    // Low-power background: cheap static gradients + grid + a few stars. No blur
    // filters, no large animated orbs/auroras, no sliding lines.
    return (
      <div style={{ position:'fixed', inset:0, zIndex:0, pointerEvents:'none', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0,
          background:'radial-gradient(ellipse at 50% 30%, #140b3a 0%, #0a0818 55%, #020108 100%)' }} />
        <div style={{ position:'absolute', inset:0,
          background:'radial-gradient(circle at 18% 12%, rgba(255,100,0,.05) 0%, transparent 45%),radial-gradient(circle at 82% 88%, rgba(100,50,220,.05) 0%, transparent 45%)' }} />
        {Array.from({length:16}, (_,i) => {
          const x = (i * 137.508) % 100;
          const y = (i * 97.31 + 13) % 100;
          const size = i % 4 === 0 ? 2.4 : 1.4;
          return (
            <div key={i} style={{ position:'absolute', left:`${x}%`, top:`${y}%`,
              width:size, height:size, borderRadius:'50%', background:'#fff', opacity:.25,
              animation:`tvStar ${3 + (i*.37)%4}s ease ${(i*.4)%6}s infinite` }} />
          );
        })}
        <div style={{ position:'absolute', inset:0,
          backgroundImage:`linear-gradient(rgba(255,255,255,.016) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.016) 1px,transparent 1px)`,
          backgroundSize:'80px 80px' }} />
      </div>
    );
  }
  return (
    <div style={{ position:'fixed', inset:0, zIndex:0, pointerEvents:'none', overflow:'hidden' }}>
      <div style={{ position:'absolute', inset:0,
        background:'radial-gradient(ellipse at 50% 30%, #140b3a 0%, #0a0818 55%, #020108 100%)' }} />
      <div style={{ position:'absolute', top:'15%', left:'-20%', right:'-20%', height:'35%',
        background:'linear-gradient(180deg, transparent, rgba(60,20,140,.2) 40%, rgba(30,100,180,.14) 60%, transparent)',
        transform:'rotate(-8deg)', filter:'blur(40px)',
        animation:'tvAurora 14s ease infinite' }} />
      <div style={{ position:'absolute', bottom:'10%', left:'-20%', right:'-20%', height:'28%',
        background:'linear-gradient(180deg, transparent, rgba(0,140,200,.1) 50%, transparent)',
        transform:'rotate(5deg)', filter:'blur(50px)',
        animation:'tvAurora 18s ease infinite 5s' }} />
      <div style={{ position:'absolute', width:'90vw', height:'90vw', top:'-35%', left:'-30%',
        background:'radial-gradient(circle, rgba(255,100,0,.06) 0%, transparent 60%)',
        animation:'tvOrb1 20s ease infinite' }} />
      <div style={{ position:'absolute', width:'80vw', height:'80vw', bottom:'-30%', right:'-25%',
        background:'radial-gradient(circle, rgba(100,50,220,.06) 0%, transparent 60%)',
        animation:'tvOrb2 25s ease infinite 4s' }} />
      <div style={{ position:'absolute', width:'60vw', height:'60vw', top:'30%', left:'30%',
        background:'radial-gradient(circle, rgba(0,180,220,.04) 0%, transparent 60%)',
        animation:'tvOrb3 30s ease infinite 10s' }} />
      {Array.from({length:80}, (_,i) => {
        const x = (i * 137.508) % 100;
        const y = (i * 97.31 + 13) % 100;
        const size = i % 5 === 0 ? 3 : i % 3 === 0 ? 2 : 1.2;
        return (
          <div key={i} style={{ position:'absolute', left:`${x}%`, top:`${y}%`,
            width:size, height:size, borderRadius:'50%', background:'#fff', opacity:.2,
            animation:`tvStar ${2 + (i*.37)%4}s ease ${(i*.4)%6}s infinite` }} />
        );
      })}
      <div style={{ position:'absolute', inset:0,
        backgroundImage:`linear-gradient(rgba(255,255,255,.016) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.016) 1px,transparent 1px)`,
        backgroundSize:'80px 80px' }} />
      <div style={{ position:'absolute', top:'32%', left:0, right:0, height:1,
        background:'linear-gradient(90deg,transparent,rgba(255,179,0,.1),rgba(0,200,220,.07),transparent)',
        animation:'tvSlide 16s linear infinite' }} />
      <div style={{ position:'absolute', top:'65%', left:0, right:0, height:1,
        background:'linear-gradient(90deg,transparent,rgba(180,60,220,.07),rgba(0,180,255,.05),transparent)',
        animation:'tvSlide 22s linear infinite 7s' }} />
    </div>
  );
}

// ─── Standby Screen ───────────────────────────────────────────────────────────
// ─── Standby Leaderboard (active org's lifetime board, rotated between games) ──
function StandbyLeaderboard() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(0);

  const aliveRef = useRef(true);
  const fetchLb = useCallback(() => {
    fetch(`${API}/leaderboard/active`).then(r => r.json())
      .then(d => { if (aliveRef.current) setData(d); }).catch(() => {});
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    fetchLb();
    const iv = setInterval(fetchLb, 120000); // slow fallback — push is primary
    return () => { aliveRef.current = false; clearInterval(iv); };
  }, [fetchLb]);

  // Active org switched / new results recorded — refresh instantly.
  useAblyChannel('crossword:admin', { 'org-leaderboard-updated': fetchLb });

  const lb    = data?.leaderboard || [];
  const PAGE  = 5;
  const pages = Math.max(1, Math.ceil(lb.length / PAGE));

  useEffect(() => {
    if (pages <= 1) { setPage(0); return; }
    const iv = setInterval(() => setPage(p => (p + 1) % pages), 7000);
    return () => clearInterval(iv);
  }, [pages]);

  if (!data?.org || lb.length === 0) return null;
  const slice = lb.slice(page * PAGE, page * PAGE + PAGE);
  const medal = (rank) => ['🥇', '🥈', '🥉'][rank] || `${rank + 1}`;

  return (
    <div style={{
      background: 'rgba(0,0,0,.4)', border: '1px solid rgba(255,179,0,.25)',
      borderRadius: 20, padding: 'clamp(16px,2.4vh,26px) clamp(24px,3.5vw,44px)',
      width: 'min(640px,86vw)', animation: 'tvFadeIn .6s ease both',
    }}>
      <div style={{ textAlign: 'center', fontSize: 'clamp(12px,1.4vw,17px)', color: 'rgba(255,179,0,.8)',
        fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 5, fontWeight: 700, marginBottom: 'clamp(10px,1.6vh,18px)' }}>
        🏆 {data.org.name} — LIFETIME LEADERBOARD
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(5px,.8vh,10px)' }}>
        {slice.map((p, i) => {
          const rank = page * PAGE + i;
          return (
            <div key={p.name + rank} style={{
              display: 'flex', alignItems: 'center', gap: 'clamp(10px,1.5vw,18px)',
              padding: 'clamp(7px,1vh,12px) clamp(12px,1.8vw,20px)',
              background: rank === 0 ? 'rgba(255,215,0,.12)' : 'rgba(255,255,255,.04)',
              border: `1px solid ${rank === 0 ? 'rgba(255,215,0,.3)' : 'rgba(255,255,255,.07)'}`,
              borderRadius: 12,
            }}>
              <span style={{ fontSize: 'clamp(18px,2.4vw,30px)', minWidth: 'clamp(30px,3vw,44px)', textAlign: 'center',
                fontWeight: 800, fontFamily: "'Bebas Neue',sans-serif",
                color: rank === 0 ? '#FFD700' : rank === 1 ? '#C0C0C0' : rank === 2 ? '#CD7F32' : 'rgba(255,255,255,.5)' }}>{medal(rank)}</span>
              <span style={{ flex: 1, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                fontSize: 'clamp(16px,2.2vw,30px)', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
              <span style={{ fontSize: 'clamp(10px,1.1vw,14px)', color: 'rgba(255,255,255,.35)',
                fontFamily: "'Barlow Condensed',sans-serif" }}>{p.gamesPlayed}g · {p.wins}w</span>
              <span style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 'clamp(20px,2.6vw,36px)',
                color: '#FFB300', letterSpacing: 1, minWidth: 'clamp(54px,6vw,90px)', textAlign: 'right' }}>{p.totalScore}</span>
            </div>
          );
        })}
      </div>
      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 'clamp(8px,1.2vh,14px)' }}>
          {Array.from({ length: pages }, (_, i) => (
            <div key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: i === page ? '#FFB300' : 'rgba(255,255,255,.2)' }} />
          ))}
        </div>
      )}
    </div>
  );
}

function StandbyScreen({ pendingGame, pendingCountdown }) {
  const [time, setTime] = useState(() => new Date().toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' }));
  useEffect(() => {
    const iv = setInterval(() => setTime(new Date().toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })), 10000);
    return () => clearInterval(iv);
  }, []);

  return (
    <div style={{ position:'fixed', inset:0, display:'flex', flexDirection:'column',
      alignItems:'center', justifyContent:'center', overflow:'hidden' }}>
      <style>{TV_STYLES}</style>
      <TvBg />

      <div style={{ position:'relative', zIndex:10, textAlign:'center',
        display:'flex', flexDirection:'column', alignItems:'center', gap:'clamp(16px,3vh,32px)',
        animation:'tvFadeIn 1s ease both', padding:'0 clamp(24px,5vw,80px)' }}>

        <div style={{
          width:'clamp(90px,10vw,140px)', height:'clamp(90px,10vw,140px)', borderRadius:32,
          background:'linear-gradient(135deg, #FF8C00, #FFB300)',
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:'clamp(48px,6vw,72px)', boxShadow:'0 0 80px rgba(255,140,0,.6)',
          animation:'tvBounce 3s ease infinite',
        }}>🎯</div>

        <div>
          <div style={{ fontSize:'clamp(48px,6vw,80px)', fontWeight:700, fontFamily:"'Bebas Neue',sans-serif",
            color:'#fff', letterSpacing:8, lineHeight:1,
            textShadow:'0 0 40px rgba(255,179,0,.5)', animation:'tvGlow 4s ease infinite' }}>
            LIVE GAME SHOW
          </div>
          <div style={{ fontSize:'clamp(13px,1.5vw,18px)', color:'rgba(255,179,0,.6)',
            fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:8, fontWeight:700,
            marginTop:6 }}>
            POWERED BY WHATSAPP
          </div>
        </div>

        {pendingGame ? (
          <div style={{
            background:'rgba(0,0,0,.5)', backdropFilter:'blur(24px)',
            border:'2px solid rgba(255,179,0,.4)', borderRadius:24,
            padding:'clamp(20px,3vh,32px) clamp(36px,5vw,60px)', textAlign:'center',
            boxShadow:'0 0 60px rgba(255,140,0,.2)',
            animation:'tvFadeIn .6s ease both',
          }}>
            <div style={{ fontSize:'clamp(12px,1.3vw,15px)', color:'rgba(255,179,0,.7)',
              fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:5,
              fontWeight:700, marginBottom:12 }}>🎮 NEW GAME READY</div>
            {pendingCountdown !== null && (
              <div style={{ fontSize:'clamp(60px,9vw,96px)', fontWeight:900,
                fontFamily:"'Bebas Neue',sans-serif",
                color:'#FFB300', letterSpacing:4,
                textShadow:'0 0 40px rgba(255,179,0,.9)',
                animation:'tvCountBig .6s cubic-bezier(.34,1.56,.64,1)' }}>
                {pendingCountdown}
              </div>
            )}
            <div style={{ fontSize:'clamp(15px,1.8vw,20px)', color:'rgba(255,255,255,.5)',
              fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:3 }}>
              Starting automatically…
            </div>
          </div>
        ) : (
          <div style={{
            background:'rgba(0,0,0,.4)', backdropFilter:'blur(16px)',
            border:'1px solid rgba(255,255,255,.08)', borderRadius:20,
            padding:'clamp(16px,2.5vh,24px) clamp(32px,4vw,52px)', textAlign:'center',
          }}>
            <div style={{ fontSize:'clamp(13px,1.5vw,17px)', color:'rgba(255,255,255,.3)',
              fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:4,
              fontWeight:600, animation:'tvPulse 3s ease infinite' }}>
              AWAITING NEXT GAME
            </div>
            <div style={{ fontSize:'clamp(28px,4vw,44px)', fontWeight:700, fontFamily:"'Barlow Condensed',sans-serif",
              color:'rgba(255,255,255,.12)', letterSpacing:4, marginTop:4 }}>
              {time}
            </div>
          </div>
        )}

        <StandbyLeaderboard />

        <div style={{
          width:'min(360px, 80vw)', height:1,
          background:'linear-gradient(90deg,transparent,rgba(255,179,0,.3),transparent)',
        }} />

        <div style={{ fontSize:'clamp(12px,1.3vw,15px)', color:'rgba(255,255,255,.2)',
          fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:4,
          fontWeight:600, maxWidth:'min(500px,80vw)', textAlign:'center', lineHeight:1.8 }}>
          This display switches to the game board automatically when a game starts.
          <br/>Send answers via WhatsApp — no app download needed.
        </div>
      </div>
    </div>
  );
}

// ─── New Game Transition Banner ───────────────────────────────────────────────
function NewGameBanner({ countdown, gameType }) {
  return (
    <div style={{
      position:'fixed', inset:0, zIndex:500, pointerEvents:'none',
      display:'flex', alignItems:'center', justifyContent:'center',
    }}>
      <div style={{
        background:'rgba(5,3,18,.92)', backdropFilter:'blur(32px)',
        border:'2px solid rgba(255,179,0,.5)', borderRadius:28,
        padding:'clamp(28px,4vh,48px) clamp(48px,7vw,96px)', textAlign:'center',
        boxShadow:'0 0 100px rgba(255,140,0,.3)',
        animation:'tvFadeIn .4s ease both',
      }}>
        <div style={{ fontSize:'clamp(14px,1.8vw,20px)', color:'rgba(255,179,0,.7)',
          fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:6,
          fontWeight:700, marginBottom:8 }}>
          {gameType === 'americasays' ? '🎯 AMERICA SAYS' : gameType === 'geoguess' ? '🗺️ GEOGUESS' : gameType === 'droppan' ? '🎴 DROP PAN' : gameType === 'chainreaction' ? '🔗 CHAIN REACTION' : gameType === 'bracket' ? '🏆 BRACKET' : '✏️ CROSSWORD'} — NEW GAME
        </div>
        <div style={{ fontSize:'clamp(64px,10vw,100px)', fontWeight:900, fontFamily:"'Bebas Neue',sans-serif",
          color:'#FFB300', letterSpacing:4,
          textShadow:'0 0 60px rgba(255,179,0,1)', lineHeight:1,
          animation:'tvCountBig .5s cubic-bezier(.34,1.56,.64,1)' }}>
          {countdown}
        </div>
        <div style={{ fontSize:'clamp(14px,1.8vw,18px)', color:'rgba(255,255,255,.4)',
          fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:4 }}>
          SWITCHING NOW…
        </div>
      </div>
    </div>
  );
}

// ─── Loading Spinner ──────────────────────────────────────────────────────────
function LoadingScreen() {
  return (
    <div style={{ position:'fixed', inset:0, display:'flex', alignItems:'center',
      justifyContent:'center', background:'#020108' }}>
      <style>{TV_STYLES}</style>
      <TvBg />
      <div style={{ position:'relative', zIndex:10, textAlign:'center',
        animation:'tvFadeIn .6s ease both' }}>
        <div style={{ fontSize:'clamp(40px,6vw,60px)', marginBottom:18,
          animation:'tvWobble 1.4s ease infinite' }}>🎯</div>
        <div style={{ fontSize:'clamp(16px,1.8vw,22px)', color:'rgba(255,255,255,.3)',
          fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:5,
          fontWeight:700, animation:'tvPulse 1.4s ease infinite' }}>
          CONNECTING…
        </div>
      </div>
    </div>
  );
}

// ─── Broadcast Overlay ────────────────────────────────────────────────────────
function BroadcastOverlay({ broadcast, onDismiss }) {
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    if (!broadcast) return;
    const endAt = broadcast.ts + broadcast.durationMs;
    const update = () => {
      const rem = Math.max(0, endAt - Date.now());
      setTimeLeft(Math.ceil(rem / 1000));
      if (rem <= 0) onDismiss();
    };
    update();
    const iv = setInterval(update, 200);
    return () => clearInterval(iv);
  }, [broadcast, onDismiss]);

  if (!broadcast) return null;

  return (
    <div style={{
      position:       'fixed',
      inset:           0,
      zIndex:          950,
      background:     'rgba(4,6,14,.82)',
      backdropFilter: 'blur(18px)',
      display:        'flex',
      flexDirection:  'column',
      alignItems:     'center',
      justifyContent: 'center',
      gap:            'clamp(12px,3vh,36px)',
      padding:        'clamp(20px,4vw,60px)',
      animation:      'tvFadeIn .35s ease both',
    }}>
      <style>{`@keyframes bcPulse{0%,100%{opacity:1}50%{opacity:.4}} @keyframes bcIn{from{opacity:0;transform:scale(.92)}to{opacity:1;transform:scale(1)}}`}</style>

      {/* Icon */}
      <div style={{
        fontSize:   'clamp(48px,8vw,88px)',
        lineHeight:  1,
        animation:  'tvBounce 2.5s ease infinite',
      }}>📢</div>

      {/* Message */}
      <div style={{
        fontFamily:   "'Bebas Neue', sans-serif",
        fontSize:     'clamp(32px,7vw,96px)',
        letterSpacing: 6,
        color:         '#fff',
        textAlign:    'center',
        lineHeight:    1.1,
        maxWidth:     '90vw',
        textShadow:   '0 0 60px rgba(255,179,0,.5)',
        animation:    'bcIn .4s cubic-bezier(.34,1.4,.64,1) both',
      }}>
        {broadcast.message}
      </div>

      {/* Countdown pill */}
      <div style={{
        fontFamily:   "'Barlow Condensed', sans-serif",
        fontWeight:    700,
        fontSize:     'clamp(14px,2vw,22px)',
        letterSpacing: 4,
        color:        'rgba(255,255,255,.4)',
        textTransform: 'uppercase',
        animation:    'bcPulse 1s ease infinite',
      }}>
        RESUMING IN {timeLeft}s
      </div>
    </div>
  );
}

// ─── Sound hint chip ──────────────────────────────────────────────────────────
// The plain /tv (PA-PC audio sink) can't autoplay sound without a user gesture,
// and we no longer block the whole board behind a splash. Instead show a small,
// unobtrusive chip: the first real gesture (anywhere) grants the browser's audio
// activation, we flash a "sound on" confirm, then it fades away. It also
// auto-dismisses after a while so it never lingers on an unattended screen.
// LITE (FireTV) autoplays audio already and has no pointer — so it never shows.
function SoundChip() {
  const [phase,   setPhase]   = useState('prompt'); // 'prompt' | 'confirm' | 'gone'
  const [visible, setVisible] = useState(false);    // drives the fade

  useEffect(() => {
    if (LITE) return undefined;
    let fadeT, confirmT, fired = false;
    const raf = requestAnimationFrame(() => setVisible(true)); // fade in

    const hide = () => { setVisible(false); fadeT = setTimeout(() => setPhase('gone'), 500); };

    const onGesture = () => {
      if (fired) return;
      fired = true;
      // A trusted gesture has now activated the document, so the boards' own
      // audio .play() calls will succeed. Nudge the audio subsystem awake too.
      try { const a = new Audio(); a.muted = true; a.play().catch(() => {}); } catch { /* ignore */ }
      setPhase('confirm');
      confirmT = setTimeout(hide, 2600);
    };
    const opts = { once: true, passive: true };
    window.addEventListener('pointerdown', onGesture, opts);
    window.addEventListener('keydown',     onGesture, opts);
    window.addEventListener('touchstart',  onGesture, opts);

    // Fade the prompt away on its own after 30s even if nobody interacts.
    const idleT = setTimeout(() => { if (!fired) hide(); }, 30000);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(fadeT); clearTimeout(confirmT); clearTimeout(idleT);
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('keydown',     onGesture);
      window.removeEventListener('touchstart',  onGesture);
    };
  }, []);

  if (LITE || phase === 'gone') return null;
  const confirmed = phase === 'confirm';
  return (
    <div style={{
      position:'fixed', right:'clamp(14px,1.6vw,26px)', bottom:'clamp(14px,1.8vh,26px)',
      zIndex:995, pointerEvents:'none',
      display:'flex', alignItems:'center', gap:'clamp(7px,.7vw,11px)',
      padding:'clamp(8px,1vh,12px) clamp(14px,1.4vw,20px)', borderRadius:999,
      background: confirmed ? 'rgba(6,40,20,.86)' : 'rgba(10,8,26,.82)',
      border:`1px solid ${confirmed ? 'rgba(0,200,110,.55)' : 'rgba(255,179,0,.4)'}`,
      boxShadow:'0 10px 30px rgba(0,0,0,.5)', backdropFilter:'blur(10px)',
      color:'#fff', fontFamily:"'Barlow Condensed',sans-serif", fontWeight:600,
      fontSize:'clamp(13px,1.3vw,18px)', letterSpacing:2, whiteSpace:'nowrap',
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(12px)',
      transition:'opacity .5s ease, transform .5s cubic-bezier(.34,1.4,.64,1)',
    }}>
      <span style={{ fontSize:'1.25em' }}>{confirmed ? '🔊' : '🔇'}</span>
      {confirmed ? 'SOUND ON' : 'TAP ANYWHERE FOR SOUND'}
    </div>
  );
}

// ─── Main TVDisplay Component ─────────────────────────────────────────────────
export default function TVDisplay() {
  const [mode,    setMode]    = useState('loading'); // loading | standby | game
  const [gameInfo, setGameInfo] = useState(null);     // { gameId, gameType }
  const [pendingGame, setPendingGame] = useState(null);
  const [transitionCountdown, setTransitionCountdown] = useState(null);
  const [blackout, setBlackout] = useState(false);   // fade-to-black between mode switches
  const [broadcast, setBroadcast] = useState(null);  // { message, durationMs, ts }
  const broadcastTimerRef = useRef(null);
  const transitionRef = useRef(null);
  const pendingRef    = useRef(null);
  const modeRef       = useRef('loading');
  const gameInfoRef   = useRef(null);

  // Keep refs in sync for use inside Ably callbacks (avoids stale closures)
  useEffect(() => { modeRef.current    = mode;     }, [mode]);
  useEffect(() => { gameInfoRef.current = gameInfo; }, [gameInfo]);

  // ── Find the best game to display ──────────────────────────────────────────
  const findBestGame = useCallback(async () => {
    try {
      const [cwRes, asRes, ggRes, dpRes, crRes, bkRes] = await Promise.all([
        fetch(`${API}/games`).then(r => r.json()).catch(() => ({ games: [] })),
        fetch(`${API}/americasays`).then(r => r.json()).catch(() => ({ games: [] })),
        fetch(`${API}/geoguess`).then(r => r.json()).catch(() => ({ games: [] })),
        fetch(`${API}/droppan`).then(r => r.json()).catch(() => ({ games: [] })),
        fetch(`${API}/chainreaction`).then(r => r.json()).catch(() => ({ games: [] })),
        fetch(`${API}/bracket`).then(r => r.json()).catch(() => ({ games: [] })),
      ]);
      // crossword /api/games returns a Firebase object keyed by gameId;
      // other game types already return arrays — Object.values handles both
      const all = [
        ...Object.values(cwRes.games  || {}).map(g => ({ ...g, gameType: 'crossword'   })),
        ...(asRes.games  || []).map(g => ({ ...g, gameType: 'americasays' })),
        ...(ggRes.games  || []).map(g => ({ ...g, gameType: 'geoguess'    })),
        ...(dpRes.games  || []).map(g => ({ ...g, gameType: 'droppan'     })),
        ...(crRes.games  || []).map(g => ({ ...g, gameType: 'chainreaction' })),
        ...(bkRes.games  || []).map(g => ({ ...g, gameType: 'bracket'       })),
      ].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

      // Prefer active games over waiting games
      const best = all.find(g => g.status === 'active')
                || all.find(g => g.status === 'waiting');

      if (best) {
        setGameInfo({ gameId: best.id, gameType: best.gameType });
        setMode('game');
      } else {
        setGameInfo(null);
        setMode('standby');
      }
    } catch {
      setGameInfo(null);
      setMode('standby');
    }
  }, []);

  // Initial load — show loading screen briefly while polling
  useEffect(() => {
    setMode('loading');
    findBestGame();
  }, [findBestGame]);

  // ── Countdown helper (NewGameBanner + fade on final switch) ─────────────────
  const launchNewGame = useCallback((info) => {
    if (transitionRef.current) { clearInterval(transitionRef.current); transitionRef.current = null; }
    let secs = 4;
    setTransitionCountdown(secs);
    transitionRef.current = setInterval(() => {
      secs -= 1;
      if (secs <= 0) {
        clearInterval(transitionRef.current);
        transitionRef.current = null;
        setTransitionCountdown(null);
        setPendingGame(null);
        pendingRef.current = null;
        // Brief blackout as the new board mounts (banner already covered the screen)
        setBlackout(true);
        setTimeout(() => {
          setGameInfo(info);
          setMode('game');
          setBlackout(false);
        }, 200);
      } else {
        setTransitionCountdown(secs);
      }
    }, 1000);
  }, []);

  // ── Go to standby with fade-to-black transition ────────────────────────────
  const goStandby = useCallback(() => {
    if (transitionRef.current) { clearInterval(transitionRef.current); transitionRef.current = null; }
    setTransitionCountdown(null);
    setPendingGame(null);
    pendingRef.current = null;
    // Fade to black, switch, fade back in
    setBlackout(true);
    setTimeout(() => {
      setGameInfo(null);
      setMode('standby');
      setBlackout(false);
      setTimeout(findBestGame, 3000);
    }, 320);
  }, [findBestGame]);

  const dismissBroadcast = useCallback(() => {
    setBroadcast(null);
    if (broadcastTimerRef.current) { clearTimeout(broadcastTimerRef.current); broadcastTimerRef.current = null; }
  }, []);

  // ── Admin channel: game lifecycle events ───────────────────────────────────
  useAblyChannel('crossword:admin', {
    'admin-broadcast': (msg) => {
      const d = msg.data || msg;
      if (broadcastTimerRef.current) clearTimeout(broadcastTimerRef.current);
      setBroadcast({ message: d.message, durationMs: d.durationMs || 10000, ts: d.ts || Date.now() });
      broadcastTimerRef.current = setTimeout(() => { setBroadcast(null); broadcastTimerRef.current = null; }, (d.durationMs || 10000) + 500);
    },
    // Game creation: always switch to the newly created game so the TV is
    // always live with whatever the admin just set up, regardless of what
    // was previously showing.
    'game-created': (msg) => {
      const info = { gameId: msg.data.gameId, gameType: 'crossword' };
      pendingRef.current = info;
      setPendingGame(info);
      launchNewGame(info);
    },
    'americasays-created': (msg) => {
      const info = { gameId: msg.data.gameId, gameType: 'americasays' };
      pendingRef.current = info;
      setPendingGame(info);
      launchNewGame(info);
    },
    'geoguess-created': (msg) => {
      const info = { gameId: msg.data.gameId, gameType: 'geoguess' };
      pendingRef.current = info;
      setPendingGame(info);
      launchNewGame(info);
    },
    'droppan-game-created': (msg) => {
      const info = { gameId: msg.data.gameId, gameType: 'droppan' };
      pendingRef.current = info;
      setPendingGame(info);
      launchNewGame(info);
    },
    'chainreaction-created': (msg) => {
      const info = { gameId: msg.data.gameId, gameType: 'chainreaction' };
      pendingRef.current = info;
      setPendingGame(info);
      launchNewGame(info);
    },
    'bracket-game-created': (msg) => {
      const info = { gameId: msg.data.gameId, gameType: 'bracket' };
      pendingRef.current = info;
      setPendingGame(info);
      launchNewGame(info);
    },

    // Game started: fires when admin clicks Start on any game type.
    // The game-specific board already handles waiting→active via its own channel,
    // but TVDisplay needs this to react when it's on standby or showing a
    // different game, and to support multiple simultaneous games correctly.
    'game-started': (msg) => {
      const { gameId, gameType } = msg.data;
      const currentId   = gameInfoRef.current?.gameId;
      const currentMode = modeRef.current;

      // Already showing this exact game — the mounted board handles the
      // waiting→active transition via its own Ably channel subscription.
      if (currentMode === 'game' && currentId === gameId) return;

      const info = { gameId, gameType };

      if (currentMode === 'standby') {
        // Nothing was showing — switch immediately (no countdown, game is live).
        setBlackout(true);
        setTimeout(() => {
          setGameInfo(info);
          setMode('game');
          setBlackout(false);
        }, 200);
        return;
      }

      // Showing a different game: re-run findBestGame so the active game
      // wins over any still-waiting game. If the current game is already
      // active we leave it alone (findBestGame prefers the most-recently-
      // created active game, so both games stay reachable in rotation).
      findBestGame();
    },

    // Game deletion: use gameInfoRef (not a state updater) — calling side
    // effects inside a setState updater is a React anti-pattern and causes
    // the transition to never fire reliably.
    'game-deleted': (msg) => {
      if (gameInfoRef.current?.gameId === msg.data?.gameId &&
          gameInfoRef.current?.gameType === 'crossword') {
        goStandby();
      }
    },
    'americasays-deleted': (msg) => {
      if (gameInfoRef.current?.gameId === msg.data?.gameId &&
          gameInfoRef.current?.gameType === 'americasays') {
        goStandby();
      }
    },
    'geoguess-deleted': (msg) => {
      if (gameInfoRef.current?.gameId === msg.data?.gameId &&
          gameInfoRef.current?.gameType === 'geoguess') {
        goStandby();
      }
    },
    'droppan-deleted': (msg) => {
      if (gameInfoRef.current?.gameId === msg.data?.gameId &&
          gameInfoRef.current?.gameType === 'droppan') {
        goStandby();
      }
    },
    'chainreaction-deleted': (msg) => {
      if (gameInfoRef.current?.gameId === msg.data?.gameId &&
          gameInfoRef.current?.gameType === 'chainreaction') {
        goStandby();
      }
    },
    'bracket-deleted': (msg) => {
      if (gameInfoRef.current?.gameId === msg.data?.gameId &&
          gameInfoRef.current?.gameType === 'bracket') {
        goStandby();
      }
    },
  });

  // ── Periodic re-poll: if we're showing a game, poll occasionally so a missed
  // Ably event doesn't leave the TV stuck. Ably is the primary signal — keep
  // this slow, every poll is a Firebase download on the free tier.
  useEffect(() => {
    if (mode !== 'game' || !gameInfo?.gameId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const apiPath = gameInfo.gameType === 'americasays'
          ? `${API}/americasays/${gameInfo.gameId}`
          : gameInfo.gameType === 'geoguess'
            ? `${API}/geoguess/${gameInfo.gameId}`
            : gameInfo.gameType === 'droppan'
              ? `${API}/droppan/${gameInfo.gameId}`
              : gameInfo.gameType === 'chainreaction'
                ? `${API}/chainreaction/${gameInfo.gameId}`
                : gameInfo.gameType === 'bracket'
                  ? `${API}/bracket/${gameInfo.gameId}`
                  : `${API}/games/${gameInfo.gameId}`;
        const res = await fetch(apiPath);
        if (!res.ok) { if (!cancelled) goStandby(); return; }
        const g = await res.json();
        // If game is now gone or completed, go standby.
        if (!cancelled && (g.status === 'deleted' || !g)) goStandby();
      } catch { /* ignore transient failures */ }
    };
    const iv = setInterval(poll, 20000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [mode, gameInfo, goStandby]);

  // ── Standby countdown display ───────────────────────────────────────────────
  const [standbyCd, setStandbyCd] = useState(null);
  useEffect(() => {
    if (mode !== 'standby' || !pendingGame) { setStandbyCd(null); return; }
    let secs = 8;
    setStandbyCd(secs);
    const iv = setInterval(() => {
      secs -= 1;
      if (secs <= 0) { clearInterval(iv); setStandbyCd(null); }
      else setStandbyCd(secs);
    }, 1000);
    return () => clearInterval(iv);
  }, [mode, pendingGame]);

  // Cleanup on unmount
  useEffect(() => () => {
    if (transitionRef.current) clearInterval(transitionRef.current);
  }, []);

  // ── Render ──────────────────────────────────────────────────────────────────
  // No tap-to-enable-audio gate: these displays run unattended in remote rooms
  // (a D-pad remote can't tap anyway), so the board must come up on its own. Sound
  // autoplays where the browser policy allows; where a gesture is required it stays
  // silent rather than blocking the whole display behind a splash nobody can dismiss.

  return (
    <>
      <style>{TV_VIEWPORT_STYLES}</style>
      {LITE_STYLES && <style>{LITE_STYLES}</style>}

      {transitionCountdown !== null && pendingGame && (
        <NewGameBanner countdown={transitionCountdown} gameType={pendingGame.gameType} />
      )}

      {mode === 'loading' && <LoadingScreen />}

      {mode === 'standby' && (
        <StandbyScreen pendingGame={pendingGame} pendingCountdown={standbyCd} />
      )}

      {mode === 'game' && gameInfo?.gameType === 'americasays' && (
        <AmericaSaysBoard
          key={gameInfo.gameId}
          gameId={gameInfo.gameId}
          tvMode={true}
          onAutoLaunch={(g) => {
            pendingRef.current = g;
            launchNewGame(g);
          }}
          onExit={() => {
            if (pendingRef.current) {
              launchNewGame(pendingRef.current);
            } else {
              goStandby();
            }
          }}
        />
      )}

      {mode === 'game' && gameInfo?.gameType === 'crossword' && (
        <CrosswordBoard
          key={gameInfo.gameId}
          gameId={gameInfo.gameId}
          tvMode={true}
          onGameCompleted={() => {
            // CrosswordBoard calls this 9s after game-state: completed
            if (pendingRef.current) {
              launchNewGame(pendingRef.current);
            } else {
              goStandby();
            }
          }}
        />
      )}

      {mode === 'game' && gameInfo?.gameType === 'geoguess' && (
        <GeoGuessBoard
          key={gameInfo.gameId}
          gameId={gameInfo.gameId}
          tvMode={true}
          onGameCompleted={() => {
            if (pendingRef.current) {
              launchNewGame(pendingRef.current);
            } else {
              goStandby();
            }
          }}
        />
      )}

      {mode === 'game' && gameInfo?.gameType === 'droppan' && (
        <DropPanBoard
          key={gameInfo.gameId}
          gameId={gameInfo.gameId}
          tvMode={true}
          onGameCompleted={() => {
            if (pendingRef.current) {
              launchNewGame(pendingRef.current);
            } else {
              goStandby();
            }
          }}
        />
      )}

      {mode === 'game' && gameInfo?.gameType === 'bracket' && (
        <BracketBoard
          key={gameInfo.gameId}
          gameId={gameInfo.gameId}
          tvMode={true}
          onGameCompleted={() => {
            if (pendingRef.current) {
              launchNewGame(pendingRef.current);
            } else {
              goStandby();
            }
          }}
        />
      )}

      {mode === 'game' && gameInfo?.gameType === 'chainreaction' && (
        <ChainReactionBoard
          key={gameInfo.gameId}
          gameId={gameInfo.gameId}
          tvMode={true}
          paused={!!broadcast}
          onGameCompleted={() => {
            if (pendingRef.current) {
              launchNewGame(pendingRef.current);
            } else {
              goStandby();
            }
          }}
        />
      )}

      {/* Global admin broadcast overlay — sits above all game boards */}
      <BroadcastOverlay broadcast={broadcast} onDismiss={dismissBroadcast} />

      {/* Sound hint chip (plain /tv only; no-op on LITE/FireTV) */}
      <SoundChip />

      {/* Fade-to-black overlay for smooth mode transitions */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 998, pointerEvents: 'none',
        background: '#020108',
        opacity: blackout ? 1 : 0,
        transition: `opacity ${blackout ? '0.25s' : '0.35s'} ease`,
      }} />
    </>
  );
}
